import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useScrollIndicator } from "@/hooks/useScrollIndicator";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { Plus, Globe, ListTodo, Pin, Square, Menu } from "lucide-react";
import { useJournals, useCreateJournal, useUpdateJournal } from "@/hooks/useJournals";
import { useSidebarContext } from "@/contexts/SidebarContext";
import type { Journal, TodoItem } from "@/types/journal";
import { AnnouncementBanner } from "@/components/announcements/AnnouncementBanner";

// Mobile menu button component - must be rendered inside AppLayout to access SidebarContext
const MobileMenuButton = () => {
  const { toggleSidebar } = useSidebarContext();
  const isTabletOrMobile = useIsTabletOrMobile();
  
  if (!isTabletOrMobile) return null;
  
  return (
    <button
      onClick={toggleSidebar}
      className="w-9 h-9 -ml-2 flex items-center justify-center text-slate-500 active:text-slate-900 active:bg-slate-100/80 rounded-lg transition-colors touch-manipulation"
      aria-label="Open menu"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
};
import CommunityJournalsCarousel from "@/components/journals/CommunityJournalsCarousel";

// ============================================================================
// Rotating Titles Component for Empty State
// ============================================================================

const ROTATING_TITLES = [
  "Document your progress.",
  "Learn out loud.",
  "Journal your learning.",
  "Learn with a personal tutor.",
  "Build your knowledge trail.",
  "Discuss. Practice. Recall.",
];

const RotatingTitle = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useRef(false);

  // Check for reduced motion preference
  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Rotate titles every 3 seconds
  useEffect(() => {
    if (prefersReducedMotion.current || isPaused) return;

    const interval = setInterval(() => {
      setIsAnimating(true);

      // After exit animation completes, switch to next title
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % ROTATING_TITLES.length);
        setIsAnimating(false);
      }, 300); // Half of total transition time
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <h1
      className="min-h-[3rem] md:min-h-[2.5rem] flex items-center justify-center"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span
        className={`text-2xl md:text-3xl font-semibold text-slate-700 transition-all duration-300 ease-in-out text-center leading-tight ${isAnimating
          ? 'opacity-0 -translate-y-1.5'
          : 'opacity-100 translate-y-0'
          }`}
      >
        {ROTATING_TITLES[currentIndex]}
      </span>
    </h1>
  );
};

// ============================================================================
// PHASE 1: Visual Refinements - Helper Functions
// ============================================================================

/**
 * Extract emoji from journal title or derive from content hints
 * Makes each journal visually distinct at a glance
 */
const getJournalIcon = (journal: Journal): string => {
  // Extract emoji from start of title if present
  const emojiMatch = journal.name.match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/u);
  if (emojiMatch) return emojiMatch[0];

  // Fallback heuristics based on title keywords
  const name = journal.name.toLowerCase();
  if (name.includes('math') || name.includes('calculus') || name.includes('algebra')) return '📐';
  if (name.includes('science') || name.includes('physics') || name.includes('chemistry')) return '🔬';
  if (name.includes('language') || name.includes('english') || name.includes('writing')) return '✍️';
  if (name.includes('korean') || name.includes('한국')) return '🇰🇷';
  if (name.includes('chinese') || name.includes('mandarin')) return '🇨🇳';
  if (name.includes('spanish') || name.includes('español')) return '🇪🇸';
  if (name.includes('japanese') || name.includes('日本')) return '🇯🇵';
  if (name.includes('history')) return '📜';
  if (name.includes('art') || name.includes('design')) return '🎨';
  if (name.includes('music')) return '🎵';
  if (name.includes('code') || name.includes('programming') || name.includes('dev')) return '💻';
  if (name.includes('biology') || name.includes('life')) return '🧬';
  if (name.includes('geography') || name.includes('world')) return '🌍';
  if (name.includes('psychology') || name.includes('brain') || name.includes('mind')) return '🧠';
  if (name.includes('business') || name.includes('finance')) return '💼';
  if (name.includes('health') || name.includes('fitness')) return '💪';
  if (name.includes('photo') || name.includes('sleep')) return '😴';

  // Default notebook emoji
  return '📓';
};

// ============================================================================
// PHASE 2: Actionable Metadata - Helper Functions
// ============================================================================

/**
 * Get pending todos count and first incomplete todo
 * Shows actionable "what's next" information on cards
 */
const getPendingTodos = (journal: Journal): { count: number; firstTodo: TodoItem | null } => {
  const todos = (journal.todo_items as unknown) as TodoItem[] | null;
  if (!todos || !Array.isArray(todos)) return { count: 0, firstTodo: null };
  const pending = todos.filter(t => !t.completed).sort((a, b) => a.order - b.order);
  return { count: pending.length, firstTodo: pending[0] || null };
};

/**
 * Convert date to human-friendly relative time
 * "Today", "Yesterday", "3 days ago", etc.
 */
const getRelativeTime = (dateString: string | null): string => {
  if (!dateString) return '';

  const now = new Date();
  const then = new Date(dateString);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return 'Last week';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return 'Last month';

  // Fall back to formatted date for older items
  return then.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Check if journal is shared to community
 */
const isJournalShared = (journal: Journal): boolean => {
  return journal.sharing_mode === 'community' || !!journal.shared_at;
};

// ============================================================================
// PHASE 3: Filter Types
// ============================================================================
type FilterType = 'all' | 'private' | 'shared';

// ============================================================================
// PHASE 4: Visual Polish - Color Palettes
// ============================================================================

const COLOR_PALETTES = {
  blue: { bg: 'bg-blue-100/35', border: 'border-blue-200/30' },
  amber: { bg: 'bg-amber-100/35', border: 'border-amber-200/30' },
  rose: { bg: 'bg-rose-100/35', border: 'border-rose-200/30' },
  emerald: { bg: 'bg-emerald-100/35', border: 'border-emerald-200/30' },
  violet: { bg: 'bg-violet-100/35', border: 'border-violet-200/30' },
  indigo: { bg: 'bg-indigo-100/35', border: 'border-indigo-200/30' },
  slate: { bg: 'bg-slate-100/35', border: 'border-slate-200/30' },
};

const CARD_COLORS = Object.values(COLOR_PALETTES);

const getJournalColor = (journal: Journal) => {
  const name = journal.name.toLowerCase();

  if (name.includes('math') || name.includes('calculus') || name.includes('algebra')) return COLOR_PALETTES.blue;
  if (name.includes('science') || name.includes('physics') || name.includes('chemistry')) return COLOR_PALETTES.emerald;
  if (name.includes('language') || name.includes('english') || name.includes('writing') || name.includes('korean') || name.includes('chinese') || name.includes('spanish') || name.includes('japanese')) return COLOR_PALETTES.rose;
  if (name.includes('history')) return COLOR_PALETTES.amber;
  if (name.includes('music')) return COLOR_PALETTES.violet;
  if (name.includes('code') || name.includes('programming') || name.includes('dev')) return COLOR_PALETTES.slate;
  if (name.includes('psychology') || name.includes('brain') || name.includes('mind')) return COLOR_PALETTES.indigo;

  // Fallback to hash-based selection
  let hash = 0;
  for (let i = 0; i < journal.id.length; i++) {
    hash = journal.id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % CARD_COLORS.length;
  return CARD_COLORS[index];
};

const SpacesPage = () => {
  const navigate = useNavigate();
  const isTabletOrMobile = useIsTabletOrMobile();
  const { isScrolling, handleScroll } = useScrollIndicator();

  // Phase 3: Filter state
  const [filter, setFilter] = useState<FilterType>('all');
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // Fetch real journals from database
  const { data: journals, isLoading } = useJournals();
  const createJournalMutation = useCreateJournal();
  const updateJournalMutation = useUpdateJournal();

  const handleSpaceClick = (journalId: string) => {
    navigate(`/journals/${journalId}`);
  };

  const handleCreateJournal = async () => {
    try {
      const newJournal = await createJournalMutation.mutateAsync({
        name: "Untitled Journal",
      });
      navigate(`/journals/${newJournal.id}`);
    } catch (error) {
      console.error("Failed to create journal:", error);
    }
  };

  // Phase 3: Toggle pin status
  const handleTogglePin = (e: React.MouseEvent, journal: Journal) => {
    e.stopPropagation();
    updateJournalMutation.mutate({
      journalId: journal.id,
      updates: { is_pinned: !journal.is_pinned }
    });
  };

  // Phase 3: Process and sort journals
  const { pinnedJournals, regularJournals, hasSharedJournals, hasPrivateJournals } = useMemo(() => {
    if (!journals) return { pinnedJournals: [], regularJournals: [], hasSharedJournals: false, hasPrivateJournals: false };

    // Sort by updated_at descending (most recent first)
    const sorted = [...journals].sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });

    // Apply filter
    const filtered = sorted.filter(j => {
      if (filter === 'all') return true;
      if (filter === 'shared') return isJournalShared(j);
      if (filter === 'private') return !isJournalShared(j);
      return true;
    });

    // Separate pinned and regular
    const pinned = filtered.filter(j => j.is_pinned);
    const regular = filtered.filter(j => !j.is_pinned);

    // Check if user has both types (for showing filter buttons)
    const hasShared = sorted.some(j => isJournalShared(j));
    const hasPrivate = sorted.some(j => !isJournalShared(j));

    return {
      pinnedJournals: pinned,
      regularJournals: regular,
      hasSharedJournals: hasShared,
      hasPrivateJournals: hasPrivate
    };
  }, [journals, filter]);

  // Render a journal card (reusable for both pinned and regular)
  const renderJournalCard = (journal: Journal, isPinned: boolean = false, animationIndex: number = 0) => {
    const journalEmoji = getJournalIcon(journal);
    const { count: pendingCount, firstTodo } = getPendingTodos(journal);
    const relativeTime = getRelativeTime(journal.updated_at);
    const isShared = isJournalShared(journal);

    // Get color theme based on subject or fallback (kept for future accent use)
    const theme = getJournalColor(journal);

    // Neutral card surface with subtle border - unified with empty state aesthetic
    // Pinned cards get a slightly stronger border
    const cardClasses = isPinned
      ? 'bg-white/90 border-slate-300/70 shadow-md'
      : 'bg-white/80 border-slate-200/60';

    return (
      <div
        key={journal.id}
        onClick={() => handleSpaceClick(journal.id)}
        className={`${cardClasses} border rounded-2xl cursor-pointer flex flex-col justify-between relative group transition-all duration-200 ease-out hover:shadow-md hover:-translate-y-px hover:border-slate-300 active:translate-y-0 active:transition-[transform] active:duration-100 animate-fade-in-up shadow-sm ${isTabletOrMobile ? 'p-4 min-h-[140px]' : 'p-5 min-h-[180px]'}`}
        style={{ animationDelay: `${animationIndex * 50}ms` }}
      >
        {/* Top Row: Emoji + Actions */}
        <div className="flex items-start justify-between">
          <span className={isTabletOrMobile ? 'text-2xl' : 'text-3xl'}>{journalEmoji}</span>

          {/* Right side: Actions - hover-only except for quiet pinned indicator */}
          <div className="flex items-center gap-1">
            {/* Shared badge - hover-only */}
            {isShared && (
              <div
                className={`p-1.5 rounded-full text-slate-400 transition-opacity duration-150 ${isTabletOrMobile ? 'opacity-60' : 'opacity-0 group-hover:opacity-100'}`}
                title="Shared to Community"
              >
                <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
              </div>
            )}

            {/* Pin button - hover-only for unpinned, quiet indicator for pinned */}
            <button
              onClick={(e) => handleTogglePin(e, journal)}
              className={`p-1.5 rounded-full transition-all duration-150 hover:bg-slate-100/80 touch-manipulation ${isPinned
                ? 'text-slate-400 opacity-60 hover:opacity-100'
                : `text-slate-400 ${isTabletOrMobile ? 'opacity-40' : 'opacity-0 group-hover:opacity-100'}`
                }`}
              title={isPinned ? 'Unpin' : 'Pin to top'}
            >
              <Pin className={`w-3.5 h-3.5 ${isPinned ? 'fill-current rotate-45' : ''}`} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Middle: Title + Meta */}
        <div className="flex-1 flex flex-col justify-end">
          <div className="space-y-2">
            <h3 className={`font-semibold text-slate-800 leading-tight truncate ${isTabletOrMobile ? 'text-sm' : 'text-base'}`}>
              {journal.name}
            </h3>
            <div className={`flex items-center gap-2 text-xs text-slate-500 transition-all duration-200 ${isTabletOrMobile ? '' : 'group-hover:opacity-0 group-hover:-translate-y-1'}`}>
              <span className="font-medium">{relativeTime}</span>
              {/* Subtle todo badge - just icon + number */}
              {pendingCount > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="flex items-center gap-1 text-slate-400 font-medium" title={`${pendingCount} pending todos`}>
                    <ListTodo className="w-3.5 h-3.5" />
                    {pendingCount}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Todo Preview - only show on desktop hover */}
        {firstTodo && !isTabletOrMobile && (
          <div className="absolute bottom-0 left-0 right-0 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 ease-out pointer-events-none px-5 pb-5">
            <div className="pt-3 border-t border-slate-200/40">
              <p className="text-xs text-slate-600 truncate flex items-center gap-2">
                <Square className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 rounded-sm" strokeWidth={1.5} />
                <span className="truncate">{firstTodo.text}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AppLayout pageTitle="Journals">
      <div
        className={`h-full bg-[#F8F8F6] bg-noise flex flex-col min-h-0 overflow-y-auto ${isScrolling ? 'scrollbar-thin-light' : 'scrollbar-hide'}`}
        onScroll={(e) => {
          handleScroll();
          setHeaderScrolled(e.currentTarget.scrollTop > 10);
        }}
      >
        <div className={`flex justify-center ${isTabletOrMobile ? 'flex-col' : 'flex-row'} gap-6 md:gap-8 px-4 sm:px-6 md:px-12 lg:px-16 py-6 md:py-12 pb-8 radial-frame`}>
          {/* Main Content */}
          <div className={`${isTabletOrMobile ? 'w-full' : 'flex-1'} min-w-0 max-w-[1600px] space-y-6 md:space-y-10 relative z-10`}>
            
            {/* Header - only show when user has journals */}
            {(regularJournals.length > 0 || pinnedJournals.length > 0) && (
              <div className={`space-y-3 sticky top-0 z-30 bg-[#F8F8F6] pt-2 pb-3 -mt-2 -mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-12 md:px-12 lg:-mx-16 lg:px-16 transition-shadow duration-200 ${headerScrolled ? 'shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)]' : ''}`}>
                {/* Title row with menu and create button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {/* Mobile menu button - fixed width for alignment */}
                    <MobileMenuButton />
                    <h1 className={`font-medium text-slate-900 ${isTabletOrMobile ? 'text-xl' : 'text-2xl -ml-px'}`}>
                      Your Journals
                    </h1>
                  </div>

                  {/* Quick Create Button */}
                  <button
                    onClick={handleCreateJournal}
                    className={`flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 active:text-slate-900 bg-white/60 hover:bg-white active:bg-white border border-slate-200/60 hover:border-slate-300 rounded-lg transition-all duration-150 touch-manipulation ${isTabletOrMobile ? 'px-2.5 py-1.5' : 'px-3 py-1.5'}`}
                  >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    <span className="hidden sm:inline">New</span>
                  </button>
                </div>

                {/* Filters row + Browse community - no extra padding, aligns with content above */}
                <div className="flex items-center justify-between text-sm">
                  {hasSharedJournals && hasPrivateJournals ? (
                    <div className="inline-flex items-center p-0.5 bg-slate-100/80 rounded-lg border border-slate-200/60">
                      {(['all', 'private', 'shared'] as FilterType[]).map((f) => (
                        <button
                          key={f}
                          onClick={() => setFilter(f)}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 touch-manipulation ${filter === f
                            ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80'
                            : 'text-slate-500 hover:text-slate-700 active:text-slate-700 border border-transparent'
                            }`}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div /> 
                  )}
                  
                  {/* Browse community link */}
                  <button
                    onClick={() => navigate('/community')}
                    className="text-slate-400 hover:text-slate-600 active:text-slate-600 transition-colors duration-150 touch-manipulation"
                  >
                    Browse community →
                  </button>
                </div>
              </div>
            )}

            {/* Announcement Banner - below filters, above journals */}
            <AnnouncementBanner className="mt-2" />

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-8 text-slate-500">
                Loading your journals...
              </div>
            )}

            {/* Pinned Journals Section - calmer styling */}
            {!isLoading && pinnedJournals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                  <Pin className="w-3 h-3 opacity-60" strokeWidth={1.5} />
                  Pinned
                </h2>
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${isTabletOrMobile ? 'gap-4' : 'gap-5'}`}>
                  {pinnedJournals.map((journal, index) => renderJournalCard(journal, true, index))}
                </div>
              </div>
            )}

            {/* Regular Journals Section */}
            {!isLoading && (
              <div className="space-y-4">
                {pinnedJournals.length > 0 && (
                  <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Recent
                  </h2>
                )}
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${isTabletOrMobile ? 'gap-4' : 'gap-5'}`}>
                  {/* Create New Learning Journal Card - matching empty state style */}
                  {(regularJournals.length > 0 || pinnedJournals.length > 0) && (
                    <div
                      onClick={handleCreateJournal}
                      className={`bg-white/80 border border-slate-200/80 rounded-2xl cursor-pointer flex flex-col items-center justify-center gap-3 animate-fade-in-up group transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-300 active:translate-y-0 active:transition-[transform] active:duration-100 ${isTabletOrMobile ? 'p-4 min-h-[140px]' : 'p-5 min-h-[180px]'}`}
                    >
                      {/* Plus Icon */}
                      <div className={`rounded-full bg-slate-100/80 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200/50 border border-transparent transition-all duration-200 ${isTabletOrMobile ? 'w-10 h-10' : 'w-12 h-12'}`}>
                        <Plus className={`text-slate-400 group-hover:text-blue-500 transition-colors duration-200 ${isTabletOrMobile ? 'w-4 h-4' : 'w-5 h-5'}`} strokeWidth={2} />
                      </div>
                      {/* CTA Text */}
                      <div className="text-center space-y-0.5">
                        <h3 className={`font-semibold text-slate-700 group-hover:text-slate-900 transition-colors ${isTabletOrMobile ? 'text-xs' : 'text-sm'}`}>
                          Create a learning journal
                        </h3>
                        <p className={`text-slate-400 group-hover:text-slate-500 transition-colors ${isTabletOrMobile ? 'text-[11px]' : 'text-xs'}`}>
                          Start with a question or video
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Regular Journal Cards */}
                  {regularJournals.map((journal, index) => renderJournalCard(journal, false, index + 1))}

                  {/* Enhanced Empty State with Community Carousel */}
                  {regularJournals.length === 0 && pinnedJournals.length === 0 && (
                    <div className="col-span-full space-y-8 md:space-y-12 pt-4 md:pt-24 pb-6">
                      {/* Mobile menu button for empty state */}
                      {isTabletOrMobile && (
                        <div className="flex justify-start -mb-4">
                          <MobileMenuButton />
                        </div>
                      )}
                      {/* Rotating titles header */}
                      <div className="text-center space-y-3 md:space-y-4 px-2">
                        <RotatingTitle />
                        <p className="text-sm text-slate-400 max-w-md mx-auto px-4">
                          Start with a topic or a community journal. StudiBudi helps you break it down, practice, and retain it.
                        </p>
                      </div>

                      {/* Create New Learning Journal Card - Centered */}
                      <div className="flex justify-center">
                        <div
                          onClick={handleCreateJournal}
                          className="bg-white/80 border border-slate-200/80 rounded-2xl p-8 cursor-pointer min-h-[200px] w-full max-w-[320px] flex flex-col items-center justify-center gap-4 animate-fade-in-up group transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-300 active:translate-y-0 active:transition-[transform] active:duration-100"
                        >
                          {/* Hero Plus Icon */}
                          <div className="w-16 h-16 rounded-full bg-slate-100/80 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200/50 border border-transparent transition-all duration-200">
                            <Plus className="w-7 h-7 text-slate-400 group-hover:text-blue-500 transition-colors duration-200" strokeWidth={2} />
                          </div>
                          {/* CTA Text */}
                          <div className="text-center space-y-1">
                            <h3 className="text-base font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
                              Create a learning journal
                            </h3>
                            <p className="text-sm text-slate-400 group-hover:text-slate-500 transition-colors">
                              Start with a question, upload, or video
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Community journals carousel */}
                      <CommunityJournalsCarousel />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SpacesPage;
